import { AHSeparator, AHText, Box } from "arkh";
import React, { useImperativeHandle, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { SetStatusFN } from "./statusCoordinator";
import { useTodoEditSetTitle } from "./useWorkspaceTodoEditSetTitle";
import { useTodoEditSetBody } from "./useWorkspaceTodoEditSetBody";
import { WorkspaceTodoEditMainContent_Todo$key } from "./__generated__/WorkspaceTodoEditMainContent_Todo.graphql";
import { ContentTitleInput } from "../../../../../components/ContentLitstItem";
import { useWorkspaceTodoEditAddFiles } from "./useWorkspaceTodoEditAddFile";

import { PendingFile } from "../../../../Files/FileUploadArea";
import cuid from "cuid";

import { WorkspaceTodoLinkedContent } from "./WorkspaceTodoLinkedContent";
import { WorkspaceTodoLinkFileModal } from "./WorkspaceTodoLinkFileModal";
import { sessionId } from "../../../../../OS/sessionId";
import { useIntlTokens } from "../../../../../components/mobile/useIntlTokens";
import { FormattedDateInWorkspace } from "@/src/common/dates/FormattedDate";
import { RichTextEditor, RichTextViewer } from "@/src/components/RichText";

export type TodoMainContentRef = {
  showFilesModal: (files: PendingFile[]) => void;
};
export const TodoMainContent = (props: {
  ref?: React.RefObject<TodoMainContentRef | null>;
  workspaceId: string;
  todo: WorkspaceTodoEditMainContent_Todo$key;
  setStatus?: SetStatusFN;
  disabled?: boolean;
  addImage?: (
    uri: string,
    { width, height }: { width: number; height: number }
  ) => void;
}) => {
  const todo = useFragment(
    graphql`
      fragment WorkspaceTodoEditMainContent_Todo on TodoWorkspace {
        id
        title
        body
        publishedAtDate {
          ...FormattedDate
        }
        publishedBy {
          id
          name
        }
        formattedBody
        updatedBySessionId
        ...WorkspaceTodoLinkedContent_todo
      }
    `,
    props.todo
  );

  const [title, setTitle] = useTodoEditSetTitle(
    todo.id,
    todo.title,
    props.setStatus ?? (() => {})
  );
  const [, setBody] = useTodoEditSetBody(
    todo.id,
    {
      body: todo.body ?? "",
      formattedBody: todo.formattedBody,
    },
    props.setStatus ?? (() => {})
  );

  const [showModal, setShowModal] = useState(false);
  const [modalKey, setModalKey] = useState(cuid());

  const openModal = () => {
    setShowModal(true);
    setModalKey(cuid());
  };

  const [initialFiles, setInitialFiles] = useState<PendingFile[]>([]);
  useImperativeHandle(props.ref, () => ({
    showFilesModal(files) {
      setInitialFiles(files);
      openModal();
    },
  }));

  const addFiles = useWorkspaceTodoEditAddFiles();
  const intl_tokens = useIntlTokens();

  return (
    <>
      <Box
        style={{ flex: 1, overflow: "hidden", borderWidth: 1 }}
        cornerRadius="large"
        borderColor="border-primary"
        color={props.disabled ? "transparent" : "surface-primary"}
      >
        {todo.publishedBy && todo.publishedAtDate ? (
          <Box
            style={{ flexDirection: "column" }}
            spaceHorizontal="medium"
            spaceTop="medium"
          >
            <Box
              style={{ flexDirection: "row", alignItems: "center", flex: 1 }}
            >
              <AHText type="detail" color="washedText">
                {todo.publishedBy.name}
              </AHText>
              <Box style={{ flex: 1 }} />
              <AHText type="detail" color="text-washed">
                <FormattedDateInWorkspace date={todo.publishedAtDate} />
              </AHText>
            </Box>
          </Box>
        ) : null}
        <ContentTitleInput
          setTitle={setTitle}
          title={title}
          autoFocus
          disabled={props.disabled}
        />
        <AHSeparator />
        {props.disabled ? (
          <Box space="small" style={{ flex: 1 }}>
            <RichTextViewer
              data={todo.formattedBody}
              unformattedData={todo.body}
              style={{
                maxHeight: 500,
              }}
            />
          </Box>
        ) : (
          <RichTextEditor
            placeholder={intl_tokens.Description}
            initialContent={todo.formattedBody}
            initialUnformattedContent={todo.body}
            disabled={props.disabled}
            onChange={setBody}
            externalSession={
              todo.updatedBySessionId
                ? todo.updatedBySessionId !== sessionId
                : false
            }
          />
        )}
        <WorkspaceTodoLinkedContent
          disabled={props.disabled}
          todo={todo}
          addImage={(uri, { width, height }) => {
            props.addImage?.(uri, { width, height });
          }}
          openLinkFile={() => {
            setShowModal(true);
          }}
        />
      </Box>

      <WorkspaceTodoLinkFileModal
        addFiles={addFiles}
        initialFiles={initialFiles}
        modalKey={modalKey}
        setShowModal={setShowModal}
        showModal={showModal}
        todoId={todo.id}
        workspaceId={props.workspaceId}
      />
    </>
  );
};
